import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import Popup from "./BasePopup";
import { generateAMPM, generateHours, generateMinutes } from "./Utils";



const DateTimePopup = ({ 
  isOpen, 
  title, 
  onClose, 
  onSave, // New prop for handling save action
  dayOptions, 
  monthOptions, 
  yearOptions 
}) => {
  const [selectedDay, setSelectedDay] = useState(dayOptions[0]);
  const [selectedMonth, setSelectedMonth] = useState(monthOptions[0]);
  const [selectedYear, setSelectedYear] = useState(yearOptions[0]);

  const [days, setDays] = useState(generateMinutes(1, monthOptions.indexOf(selectedMonth) + 1, parseInt(selectedYear)));
  const [extendedDays, setExtendedDays] = useState([...generateMinutes(1, 1, parseInt(selectedYear))]);

  const dayRef = useRef(null);
  const monthRef = useRef(null);
  const yearRef = useRef(null);

  useEffect(() => {
    const newDays = generateMinutes(1, monthOptions.indexOf(selectedMonth) + 1, parseInt(selectedYear));
    setDays(newDays);
    setExtendedDays([...newDays, ...newDays, ...newDays]); // Duplicate list for looping
  }, [selectedMonth, selectedYear]);

  const centerScroll = (container, selectedValue) => {
    const items = Array.from(container.children);
    const selectedItem = items.find(item => item.textContent === selectedValue);
    if (selectedItem) {
      const containerHeight = container.clientHeight;
      const itemHeight = selectedItem.clientHeight;
      const offsetTop = selectedItem.offsetTop;

      const newScrollTop = offsetTop - containerHeight / 2 + itemHeight / 2;
      container.scrollTop = Math.max(0, Math.min(newScrollTop, container.scrollHeight - containerHeight));
      updateItemStyles(container);
    }
  };

  const updateItemStyles = (container) => {
    const items = Array.from(container.children);
    const containerCenter = container.scrollTop + container.clientHeight / 2;

    items.forEach(item => {
      const itemCenter = item.offsetTop + item.clientHeight / 2;
      const isCentered = Math.abs(itemCenter - containerCenter) < item.clientHeight / 2;
      item.classList.toggle('selected', isCentered);
      item.classList.remove('before', 'after');
    });

    const selectedIndex = items.findIndex(item => item.classList.contains('selected'));
    if (selectedIndex > 0) items[selectedIndex - 1].classList.add('before');
    if (selectedIndex < items.length - 1) items[selectedIndex + 1].classList.add('after');
  };

  useEffect(() => {
    centerScroll(dayRef.current, selectedDay);
    centerScroll(monthRef.current, selectedMonth);
    centerScroll(yearRef.current, selectedYear);
  }, [selectedDay, selectedMonth, selectedYear]);

  useEffect(() => {
    const handleScroll = (e) => {
      const container = e.target;
      const scrollHeight = container.scrollHeight;
      const scrollTop = container.scrollTop;
      const clientHeight = container.clientHeight;

      // Check if user is scrolling near the start or end
      if (scrollTop < 100) {
        // User is at the top
        container.scrollTop += container.scrollHeight / 3;
      } else if (scrollTop + clientHeight > scrollHeight - 100) {
        // User is at the bottom
        container.scrollTop -= container.scrollHeight / 3;
      }

      updateItemStyles(container);
    };

    if (dayRef.current) dayRef.current.addEventListener('scroll', handleScroll);
    if (monthRef.current) monthRef.current.addEventListener('scroll', handleScroll);
    if (yearRef.current) yearRef.current.addEventListener('scroll', handleScroll);

    return () => {
      if (dayRef.current) dayRef.current.removeEventListener('scroll', handleScroll);
      if (monthRef.current) monthRef.current.removeEventListener('scroll', handleScroll);
      if (yearRef.current) yearRef.current.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    const checkCenterItem = () => {
      if (dayRef.current) {
        const centeredDay = Array.from(dayRef.current.children).find(item => item.classList.contains('selected'))?.textContent;
        if (centeredDay && centeredDay !== selectedDay) setSelectedDay(centeredDay);
      }
      if (monthRef.current) {
        const centeredMonth = Array.from(monthRef.current.children).find(item => item.classList.contains('selected'))?.textContent;
        if (centeredMonth && centeredMonth !== selectedMonth) setSelectedMonth(centeredMonth);
      }
      if (yearRef.current) {
        const centeredYear = Array.from(yearRef.current.children).find(item => item.classList.contains('selected'))?.textContent;
        if (centeredYear && centeredYear !== selectedYear) setSelectedYear(centeredYear);
      }
    };

    const interval = setInterval(checkCenterItem, 100); // Check every 100ms

    return () => clearInterval(interval);
  }, [selectedDay, selectedMonth, selectedYear]);

  const handleDayChange = (day) => setSelectedDay(day);
  const handleMonthChange = (month) => setSelectedMonth(month);
  const handleYearChange = (year) => setSelectedYear(year);

  const handleSave = () => {
    // Format the selected date as dd/mm/yyyy
    const formattedDate = `${selectedDay}/${monthOptions.indexOf(selectedMonth) + 1}/${selectedYear}`;
    onSave(formattedDate);
  };

  return (
    <Popup isOpen={isOpen} title={title} onClose={onClose}>
      <div className="popup-container p-0">
        <div className="custom-date-range">
          <div className="scroll-picker">
            {/* Month Picker */}
            <div className="scroll-container" ref={monthRef}>
              {monthOptions.concat(monthOptions, monthOptions).map((month, index) => (
                <div
                  key={index}
                  className={`scroll-item ${month === selectedMonth ? "selected" : ""}`}
                  onClick={() => handleMonthChange(month)}
                >
                  {month}
                </div>
              ))}
            </div>

            {/* Day Picker */}
            <div className="scroll-container" ref={dayRef}>
              {extendedDays.map((day, index) => (
                <div
                  key={index}
                  className={`scroll-item ${day === selectedDay ? "selected" : ""}`}
                  onClick={() => handleDayChange(day)}
                >
                  {day}
                </div>
              ))}
            </div>

            {/* Year Picker */}
            <div className="scroll-container" ref={yearRef}>
             
             {/* { console.log(yearOptions[0]==selectedYear)} */}
              {yearOptions.map((year, index) => (
                
                <div
                  key={index}
                  className={`scroll-item ${year === selectedYear ? "selected" : ""}`}
                  onClick={() => handleYearChange(year)}
                >
                  {year}
                </div>
              ))}
            </div>
          </div>
          <div className="active-date-range"></div>
        </div>
        <button className="submit-button mt-4" onClick={handleSave}>Save</button>
      </div>
    </Popup>
  );
};

DateTimePopup.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired, // Add this to PropTypes
  dayOptions: PropTypes.arrayOf(PropTypes.string).isRequired,
  monthOptions: PropTypes.arrayOf(PropTypes.string).isRequired,
  yearOptions: PropTypes.arrayOf(PropTypes.string).isRequired
};

DateTimePopup.defaultProps = {
  dayOptions: generateMinutes(1, 8, 1997), // Default example options
  monthOptions: generateHours(),
  yearOptions: generateAMPM()
};

export default DateTimePopup;
